import React, { useEffect } from "react";
import { useHistory } from "react-router";
import { Events, Params, Screens, useAnalytics } from "../../../AppAnalyticsProvider";
import { useAppState } from "../../../AppListenerProvider";
import { OnboardingStatus } from "../../../models/onboardingStatus";
import { OnboardingStepConfig } from "../../../models/onboardingStepConfig";
import LoadingSplash from "../../../pages/LoadingSplash";
import PhoneNumberInput from "../PhoneNumberInput/PhoneNumberInput";
import { useSignUp } from "../SignUpProvider/SignUpProvider";
import WelcomeLoginAuth from "../WelcomeLoginAuth/WelcomeLoginAuth";
import WelcomeLoginUsername from "../WelcomeLoginUsername/WelcomeLoginUsername";

interface ContainerProps {
}

const WelcomeLogin: React.FC<ContainerProps> = ({ }) => {
    const history = useHistory();
    const { step, setStep } = useSignUp();
    const { trackEvent, trackScreen } = useAnalytics();
    const { platform } = useAppState();

    const stepConfigState: Record<string, OnboardingStepConfig> = {
        [OnboardingStatus.IN_PROGRESS_LOGIN_INIT]: {
            titleText: '',
            showBackButton: false,
            showSkipButton: false,
        },
        [OnboardingStatus.IN_PROGRESS_LOGIN]: {
            titleText: 'Log In to Ecliptic',
            showBackButton: true,
            showSkipButton: false,
            onBack: async () => {
                await navigateBackToInitial();
            },
            onNext: async () => {
                history.push('/');
                await handleOnboardingStatus(OnboardingStatus.COMPLETED);
            }
        },
        [OnboardingStatus.IN_PROGRESS_LOGIN_USERNAME]: {
            titleText: 'Enter Email/Username and Password',
            showSkipButton: false,
            showBackButton: true,
            onBack: async () => {
                await handleOnboardingStatus(OnboardingStatus.IN_PROGRESS_LOGIN);
            },
            onNext: async () => {
                history.push('/');
                await handleOnboardingStatus(OnboardingStatus.COMPLETED);
            },
            onForgotPassword: async () => {
                history.push('/auth/forgot-password');
            }
        },
        [OnboardingStatus.IN_PROGRESS_LOGIN_PHONE]: {
            titleText: 'Enter Phone Number',
            showSkipButton: false,
            showBackButton: true,
            onBack: async () => {
                await handleOnboardingStatus(OnboardingStatus.IN_PROGRESS_LOGIN);
            },
            onNext: async () => {
                history.push('/');
                await handleOnboardingStatus(OnboardingStatus.COMPLETED);
            }
        }
    };

    const getScreenNameForStep = (loginStep: string): string | null => {
        switch (loginStep) {
            case OnboardingStatus.IN_PROGRESS_LOGIN:
                return Screens.LOGIN_AUTH;
            case OnboardingStatus.IN_PROGRESS_LOGIN_USERNAME:
                return Screens.LOGIN_USERNAME;
            case OnboardingStatus.IN_PROGRESS_LOGIN_PHONE:
                // Tracked granularly in PhoneNumberInput component
                return null;
            default:
                return null;
        }
    };

    const getStepNumber = (loginStep: string): number => {
        const stepOrder: Record<string, number> = {
            [OnboardingStatus.IN_PROGRESS_LOGIN_INIT]: 1,
            [OnboardingStatus.IN_PROGRESS_LOGIN]: 2,
            [OnboardingStatus.IN_PROGRESS_LOGIN_USERNAME]: 3,
            [OnboardingStatus.IN_PROGRESS_LOGIN_PHONE]: 3,
            [OnboardingStatus.COMPLETED]: 4
        };
        return stepOrder[loginStep] || 0;
    };

    const navigateBackToInitial = async () => {
        await handleOnboardingStatus(OnboardingStatus.INIT);
        history.push('/flow');
    }

    const handleOnboardingStatus = async (status: string) => {
        const newStepNumber = getStepNumber(status);
        const currentStepNumber = getStepNumber(step);
        const screenName = getScreenNameForStep(status);
        const currentStepName = getScreenNameForStep(step);

        // Track back navigation - fire-and-forget
        if (newStepNumber < currentStepNumber && currentStepName) {
            trackEvent(Events.LOGIN_STEP_BACK, {
                [Params.SCREEN_NAME]: currentStepName,
                [Params.STEP_NUMBER]: currentStepNumber
            });
        }

        // Track the new step view - fire-and-forget
        if (screenName) {
            trackScreen(screenName);
            trackEvent(Events.LOGIN_STEP_VIEW, {
                [Params.SCREEN_NAME]: screenName,
                [Params.STEP_NUMBER]: newStepNumber
            });
        }

        await setStep(status);
    }

    useEffect(() => {
        const initializeStep = async () => {
            if (step) {
                if (step.includes(OnboardingStatus.LOGIN)) {
                    console.log("VALID STATUS: " + step);
                    await handleOnboardingStatus(step);
                } else {
                    console.log("INVALID STATUS: GOING TO FLOW");
                    await handleOnboardingStatus(OnboardingStatus.IN_PROGRESS_LOGIN);
                }
            } else {
                await handleOnboardingStatus(OnboardingStatus.INIT);
                history.push("/flow");
            }
        };

        console.log("Loading Login Flow App Page");
        initializeStep();
    }, []);  // Empty dependency array means this runs once on mount

    useEffect(() => {
        const redirectBasedOnStep = async () => {
            console.log("Step: " + step);
            if (step === OnboardingStatus.IN_PROGRESS_LOGIN_INIT) {
                console.log("REDIRECTING TO LOGIN FLOW");
                await handleOnboardingStatus(OnboardingStatus.IN_PROGRESS_LOGIN);
            }
        };

        redirectBasedOnStep();
    }, [step]);

    // Track initial screen view
    useEffect(() => {
        trackScreen(Screens.LOGIN_AUTH);
    }, []);

    const renderStep = () => {
        switch (step) {
            case OnboardingStatus.IN_PROGRESS_LOGIN:
                return (
                    <WelcomeLoginAuth onBack={stepConfigState[OnboardingStatus.IN_PROGRESS_LOGIN].onBack}
                        showBackButton={stepConfigState[OnboardingStatus.IN_PROGRESS_LOGIN].showBackButton}
                        showSkipButton={stepConfigState[OnboardingStatus.IN_PROGRESS_LOGIN].showSkipButton}
                        title={stepConfigState[OnboardingStatus.IN_PROGRESS_LOGIN].titleText}
                        handleAuthedStep={stepConfigState[OnboardingStatus.IN_PROGRESS_LOGIN].onNext}
                        handlePhoneAuthStep={() => handleOnboardingStatus(OnboardingStatus.IN_PROGRESS_LOGIN_PHONE)}
                        handleUserPasswordAuthedStep={() => handleOnboardingStatus(OnboardingStatus.IN_PROGRESS_LOGIN_USERNAME)} />
                );
            case OnboardingStatus.IN_PROGRESS_LOGIN_USERNAME:
                return (
                    <WelcomeLoginUsername
                        title={stepConfigState[OnboardingStatus.IN_PROGRESS_LOGIN_USERNAME].titleText}
                        showSkipButton={stepConfigState[OnboardingStatus.IN_PROGRESS_LOGIN_USERNAME].showSkipButton}
                        showBackButton={stepConfigState[OnboardingStatus.IN_PROGRESS_LOGIN_USERNAME].showBackButton}
                        onBack={stepConfigState[OnboardingStatus.IN_PROGRESS_LOGIN_USERNAME].onBack}
                        onNext={stepConfigState[OnboardingStatus.IN_PROGRESS_LOGIN_USERNAME].onNext}
                        onForgotPassword={stepConfigState[OnboardingStatus.IN_PROGRESS_LOGIN_USERNAME].onForgotPassword}
                    />
                )
            case OnboardingStatus.IN_PROGRESS_LOGIN_PHONE:
                return (
                    <PhoneNumberInput
                        signUp={false}
                        showBackButton={stepConfigState[OnboardingStatus.IN_PROGRESS_LOGIN_PHONE].showBackButton}
                        showSkipButton={stepConfigState[OnboardingStatus.IN_PROGRESS_LOGIN_PHONE].showSkipButton}
                        title={stepConfigState[OnboardingStatus.IN_PROGRESS_LOGIN_PHONE].titleText}
                        onBack={stepConfigState[OnboardingStatus.IN_PROGRESS_LOGIN_PHONE].onBack}
                        onNext={stepConfigState[OnboardingStatus.IN_PROGRESS_LOGIN_PHONE].onNext} />
                )
            case OnboardingStatus.COMPLETED:
                return (
                    <LoadingSplash />
                );
            default:
                return null;
        }
    }

    return renderStep();
};

export default WelcomeLogin;